<template>
  <div v-if="fleetTaskStatus !== 'Dibatalkan'" class="toggle-activity">
    <b-dropdown
      v-if="items.length > 0"
      id="toggle-activity"
      class="dropdown-left"
      :disabled="disabled"
    >
      <template #button-content>
        <i class="more-icon icon-outline_more-vertical"></i>
      </template>

      <template v-for="item in items">
        <b-dropdown-item
          class="toggle-item"
          :class="item.class"
          @click="$emit('click-item', item.key)"
        >
          {{ item.label }}
        </b-dropdown-item>
      </template>
    </b-dropdown>
  </div>
</template>

<script>
import { Helper } from '@/mixin/helper'
import { mapState, mapGetters } from 'vuex'
import getRelatedActivity from '../lib/get-related-activity'

export default {
  mixins: [Helper],
  props: {
    activity: {
      type: Object,
      required: true,
    },
    fleetTaskStatus: {
      type: String,
      required: true,
    },
    pointId: {
      type: String,
      required: true,
    },
    hasTmsUserGroupAccess: {
      type: Boolean,
      default: false,
    },
    isUserGroupNull: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['click-item'],
  computed: {
    ...mapState('fleetTask', ['sequence', 'selectedDos']),
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    isPickUp() {
      return this.activity.type == 'PICKUP'
    },
    relatedActivity() {
      return getRelatedActivity(this.activity, this.sequence)
    },
    isAdjustedLoad() {
      return this.activity.delivery_order && this.activity.delivery_order.is_adjusted === true
    },
    currentDeliveryOrder() {
      return (
        this.selectedDos.find((el) => el.delivery_order_id == this.activity.delivery_order.id) || {}
      )
    },
    enabledAdjustment() {
      const deliveryOrderId = this.activity?.delivery_order?.id
      const deliveryOrderDetail = this.selectedDos.find(
        (doNumber) => doNumber.id === deliveryOrderId
      )
      const deliveryOrderDetailOrigin = deliveryOrderDetail?.origin_address?.id
      const deliveryOrderDetailDestination = deliveryOrderDetail?.destination_address?.id

      const sequenceDetail = this.sequence.find((point) => point.id === this.pointId)
      const sequenceDetailAddress = sequenceDetail?.address?.id

      const hasLoad = deliveryOrderDetail?.load_count > 0 ? true : false

      if (deliveryOrderDetail?.shipment_type === 'DIRECT') {
        if (!hasLoad) return false
        return true
      } else if (deliveryOrderDetail?.shipment_type === 'TRANSIT') {
        if (this.activity.type === 'PICKUP') {
          if (deliveryOrderDetailOrigin === sequenceDetailAddress) {
            if (!hasLoad) return false
            return true
          }
        } else if (this.activity.type === 'DROP') {
          if (deliveryOrderDetailDestination === sequenceDetailAddress) {
            if (!hasLoad) return false
            return true
          }
        }
        return false
      }
      return false
    },
    hasCompletedDrop() {
      const deliveryOrderId = this.activity?.delivery_order?.id
      if (!deliveryOrderId) return false

      if (!this.isPickUp) {
        const deliveryOrderDetail = this.selectedDos.find(
          (doNumber) => doNumber.id === deliveryOrderId
        )
        const deliveryOrderDetailDestination = deliveryOrderDetail?.destination_address?.id

        const sequenceDetail = this.sequence.find((point) => point.id === this.pointId)
        const sequenceDetailAddress = sequenceDetail?.address?.id

        if (deliveryOrderDetailDestination !== sequenceDetailAddress) {
          return false
        }
      }

      const defaultPoints = this.sequence.filter((point) => point.type === 'DEFAULT')

      return defaultPoints.some((point) =>
        point.activities.some(
          (activity) =>
            activity.type === 'DROP' &&
            activity.delivery_order?.id === deliveryOrderId &&
            activity.status?.raw_type === 'COMPLETED'
        )
      )
    },
    hasLoad() {
      const deliveryOrderDetail = this.selectedDos.find(
        (doNumber) => doNumber.id === this.activity?.delivery_order?.id
      )
      return deliveryOrderDetail?.load_count > 0 ? true : false
    },
    items() {
      let actions = []

      let canTransfer = this.isPickUp || this.activity.status.raw_type === 'UNCOMPLETED'

      const canTransferWithOrWithoutUserGroupScope =
        canTransfer && (!this.hasTmsUserGroupAccess || !this.isUserGroupNull)

      if (['Perjalanan', 'Selesai'].includes(this.fleetTaskStatus) && this.activity?.completed_on) {
        actions = [
          {
            label: this.isPickUp
              ? this.$t('fleetTask.toggleActivity.viewPickupProof')
              : this.$t('fleetTask.toggleActivity.viewDeliveryProof'),
            key: 'view_proof',
          },
        ]

        if (
          this.hasCustomFeatureSamator &&
          this.isPickUp &&
          this.isProductTypeLiquid(this.currentDeliveryOrder)
        ) {
          return actions
        }

        if (
          this.isPickUp &&
          this.hasCompletedDrop &&
          this.hasLoad &&
          !this.currentDeliveryOrder.is_from_vendor_request
        ) {
          actions.push({ label: this.$t('fleetTask.toggleActivity.viewLoad'), key: 'view_load' })
        } else if (this.enabledAdjustment && !this.currentDeliveryOrder.is_from_vendor_request) {
          actions.push({
            label: this.$t('fleetTask.toggleActivity.changeLoadQuantity'),
            key: 'load_adjustment'
          })
        }

        if (this.isAdjustedLoad) {
          actions.push({
            label: this.$t('fleetTask.toggleActivity.loadChangeHistory'),
            key: 'load_history'
          })
        }

        return actions
      }

      if (this.fleetTaskStatus === 'Perjalanan') {
        actions.push({ label: this.$t('fleetTask.toggleActivity.complete'), key: 'complete' })
      }

      if (this.currentDeliveryOrder.is_from_vendor_request) {
        return actions
      }

      if (
        this.currentDeliveryOrder.shipment_type !== 'TRANSIT' &&
        canTransferWithOrWithoutUserGroupScope
      ) {
        actions.push({ label: this.$t('fleetTask.toggleActivity.transferDelivery'), key: 'transfer' })
      }

      if (
        this.fleetTaskStatus === 'Perjalanan' &&
        this.hasOtherFleetTask &&
        this.currentDeliveryOrder.shipment_type !== 'TRANSIT'
      ) {
        actions.push({
          label: this.$t('fleetTask.toggleActivity.rescheduleDelivery'),
          key: 'rescheduled'
        })
        actions.push({
          label: this.$t('fleetTask.toggleActivity.cancelDelivery'),
          key: 'cancel',
          class: '__danger'
        })
      }

      if (
        ['Dijadwalkan', 'Perjalanan', 'Disetujui'].includes(this.fleetTaskStatus) &&
        (!this.hasTmsUserGroupAccess || (this.hasTmsUserGroupAccess && !this.isUserGroupNull))
      ) {
        const cancelIndex = actions.findIndex((a) => a.key === 'cancel')

        if (cancelIndex !== -1) {
          actions.splice(cancelIndex, 0, {
            label: this.$t('fleetTask.toggleActivity.removeDelivery'),
            key: 'remove',
            class: '__danger',
          })
        } else {
          actions.push({
            label: this.$t('fleetTask.toggleActivity.removeDelivery'),
            key: 'remove',
            class: '__danger'
          })
        }
      }

      if (['Dijadwalkan', 'Disetujui'].includes(this.fleetTaskStatus)) {
        actions.push({
          label: this.$t('fleetTask.toggleActivity.cancel'),
          key: 'cancel',
          class: '__danger'
        })
      }

      return actions
    },
    hasOtherFleetTask() {
      const deliveryOrderId = this.activity?.delivery_order?.id
      const deliveryOrderDetail = this.selectedDos.find(
        (doNumber) => doNumber.id === deliveryOrderId
      )

      if (
        deliveryOrderDetail?.shipment_type === 'TRANSIT' &&
        deliveryOrderDetail?.transits.length > 1
      ) {
        return false
      }

      return true
    },
    hasTransferDoFlag() {
      return this.getFeatureFlagValue('transferDoFlag')
    },
    hasCustomFeatureSamator() {
      const data = this.getFeatureFlagValue('customFeature.samator')
      return Array.isArray(data?.company_id) && data.company_id.includes(this.getCompanyId())
    },
  },
  methods: {
    isProductTypeLiquid(deliveryOrderDetail = {}) {
      const productTypeField = deliveryOrderDetail?.user_definition_fields?.find(
        (field) => field?.name === 'Product Type'
      )
      const productTypeValue = (productTypeField?.value || '').toString().trim().toLowerCase()

      return ['bulk', 'tube'].includes(productTypeValue)
    },
  },
}
</script>

<style scoped lang="scss">
.toggle-activity {
  .completed-icon,
  .more-icon {
    font-size: 20px;
  }

  .completed-icon {
    &::before {
      color: $success;
    }
  }
}

:deep(#toggle-activity) {
  .dropdown-toggle {
    border: none;
    background-color: transparent;
    padding: 0;

    &:hover {
      opacity: 1;
    }
  }

  .dropdown-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
  }

  .__danger {
    a {
      color: $red;
    }
  }
}

:deep(.dropdown-left .dropdown-menu.dropdown-menu) {
  top: 50% !important;
  transform: translateY(-50%) !important;
  left: auto !important;
  right: 100% !important;
}
</style>
